import {useMutation} from "@tanstack/react-query";
import {addDoc, collection} from "firebase/firestore";
import type {ZodType, z} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";
import {removeUndefined} from "~/api/deepUndefinedRemover";
import {firestore} from "~/firebase/firebase";

type CreateMutationArgs<
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
> = {
  key: CollectionArgs;
  data: Omit<z.infer<T>, "id">;
};

export const creationHookFactory = <
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
>(
  config: BaseCollectionConfig<T, CollectionArgs>,
) => {
  return () => {
    return useMutation<string, Error, CreateMutationArgs<T, CollectionArgs>>({
      mutationFn: async ({key, data}) => {
        // This function actually performs the creation
        try {
          const {id, ...parsedData} = config.parseData("", data); // No id needed for creation
          const collectionRef = collection(
            firestore,
            ...config.getCollectionPath(key),
          );

          // Add a new document to the collection, Firestore generates a new document ID
          const docRef = await addDoc(
            collectionRef,
            removeUndefined(parsedData),
          );

          // Optional: return the generated ID for further use
          return docRef.id;
        } catch (err) {
          console.error("Error creating document: ", err);
          throw err;
        }
      },
    });
  };
};
